import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Toast Context
const ToastContext = createContext(null);

// Toast types with their styles
const toastStyles = {
    success: {
        bg: 'bg-gradient-to-r from-emerald-900/90 to-emerald-800/90',
        border: 'border-emerald-500/50',
        glow: 'shadow-[0_0_20px_rgba(16,185,129,0.4),inset_0_0_20px_rgba(16,185,129,0.1)]',
        icon: (
            <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
        ),
        accent: 'text-emerald-400',
    },
    error: {
        bg: 'bg-gradient-to-r from-red-900/90 to-red-800/90',
        border: 'border-red-500/50',
        glow: 'shadow-[0_0_20px_rgba(239,68,68,0.4),inset_0_0_20px_rgba(239,68,68,0.1)]',
        icon: (
            <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
        ),
        accent: 'text-red-400',
    },
    info: {
        bg: 'bg-gradient-to-r from-indigo-900/90 to-indigo-800/90',
        border: 'border-indigo-500/50',
        glow: 'shadow-[0_0_20px_rgba(99,102,241,0.4),inset_0_0_20px_rgba(99,102,241,0.1)]',
        icon: (
            <svg className="w-5 h-5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
        accent: 'text-indigo-400',
    },
    warning: {
        bg: 'bg-gradient-to-r from-amber-900/90 to-amber-800/90',
        border: 'border-amber-500/50',
        glow: 'shadow-[0_0_20px_rgba(245,158,11,0.4),inset_0_0_20px_rgba(245,158,11,0.1)]',
        icon: (
            <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
        ),
        accent: 'text-amber-400',
    },
};

// Individual Toast Component
const ToastItem = ({ toast, onClose }) => {
    const style = toastStyles[toast.type] || toastStyles.info;

    return (
        <motion.div
            initial={{ opacity: 0, y: -50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -50, scale: 0.9 }}
            transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30,
            }}
            className={`
                relative flex items-start gap-3 p-4 min-w-[320px] max-w-[420px]
                ${style.bg} ${style.glow}
                border ${style.border}
                rounded-lg backdrop-blur-xl
                font-rajdhani
            `}
        >
            {/* Animated border glow effect */}
            <motion.div
                className={`absolute inset-0 rounded-lg border ${style.border} opacity-50 pointer-events-none`}
                animate={{
                    opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                }}
            />

            {/* Icon */}
            <div className={`flex-shrink-0 mt-0.5 ${style.accent} relative z-10`}>
                {style.icon}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 relative z-10">
                {toast.title && (
                    <h4 className={`font-orbitron text-sm font-semibold tracking-wide ${style.accent} mb-1`}>
                        {toast.title}
                    </h4>
                )}
                <p className="text-gray-200 text-sm leading-relaxed whitespace-pre-wrap">
                    {toast.message}
                </p>
            </div>

            {/* Close button */}
            <button
                onClick={() => onClose(toast.id)}
                className="flex-shrink-0 p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/10 transition-colors relative z-10 cursor-pointer"
            >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>

            {/* Progress bar */}
            {toast.duration && (
                <motion.div
                    className={`absolute bottom-0 left-0 h-0.5 ${style.accent.replace('text-', 'bg-')} rounded-full`}
                    initial={{ width: '100%' }}
                    animate={{ width: '0%' }}
                    transition={{ duration: toast.duration / 1000, ease: 'linear' }}
                />
            )}
        </motion.div>
    );
};

// Toast Container Component
export const ToastContainer = () => {
    const { toasts, removeToast } = useToast();

    return (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-3 pointer-events-none">
            <AnimatePresence mode="popLayout">
                {toasts.map((toast) => (
                    <div key={toast.id} className="pointer-events-auto">
                        <ToastItem toast={toast} onClose={removeToast} />
                    </div>
                ))}
            </AnimatePresence>
        </div>
    );
};

// Toast Provider Component
export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback((options) => {
        const id = Date.now() + Math.random();
        const duration = options.duration ?? 4000;

        const newToast = {
            id,
            type: options.type || 'info',
            title: options.title,
            message: options.message,
            duration,
        };

        setToasts((prev) => [...prev, newToast]);

        if (duration > 0) {
            setTimeout(() => {
                setToasts((prev) => prev.filter((t) => t.id !== id));
            }, duration);
        }

        return id;
    }, []);

    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const toast = {
        success: (message, options = {}) => addToast({ ...options, message, type: 'success', title: options.title || 'Success' }),
        error: (message, options = {}) => addToast({ ...options, message, type: 'error', title: options.title || 'Error' }),
        info: (message, options = {}) => addToast({ ...options, message, type: 'info', title: options.title || 'Info' }),
        warning: (message, options = {}) => addToast({ ...options, message, type: 'warning', title: options.title || 'Warning' }),
        custom: (options) => addToast(options),
    };

    return (
        <ToastContext.Provider value={{ toasts, addToast, removeToast, toast }}>
            {children}
            <ToastContainer />
        </ToastContext.Provider>
    );
};

// Custom hook to use toast
export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export default ToastProvider;
